import { NgClass } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PaginatorState } from 'primeng/paginator';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { Button } from '../../../Components/button/button';
import { CustomInput } from '../../../Components/input/custom-input';
import { SpinnerOverlayService } from '../../../Components/spinner/spinner-overlay.service';
import { ITEMS_PER_PAGE } from '../../../core/config/pagination.constants';
import { MessageService } from '../../../shared/message.service';
import { IUser } from '../../../shared/model/user.model';
import { BuyerDelete } from '../buyer-delete/buyer-delete';
import { BuyerUpdate } from '../buyer-update/buyer-update';
import { BuyerService } from './buyer.service';
import { CustomerService } from '../../../shared/service/customer-service';
import {
  ROLE_BUYER,
  ROLE_OWNER
} from '../../../core/config/role.constants';

@Component({
  selector: 'app-comprador',
  imports: [
    NgClass,
    TableModule,
    ReactiveFormsModule,
    FormsModule,
    CustomInput,
    Button
  ],
  templateUrl: './buyer.component.html',
  styleUrl: './buyer.component.css'
})
export class Buyer implements OnInit {
  compradorList: IUser[] = []
  customerNameMap: Map<number, string> = new Map();

  messageService = inject(MessageService);

  totalItems: number = 2
  first = 0;
  rows = ITEMS_PER_PAGE;
  page = 0;
  sortField = 'id';
  sortOrder = 0;
  lastEvent: any;
  user: string | null = null
  date: Date | null = null
  status: string = 'null'

  constructor(private compradorService: BuyerService, private customerService: CustomerService, private spinner: SpinnerOverlayService, private dialog: MatDialog) {
  }

  ngOnInit() {
    this.findAll()
  }

  findAll(reset: boolean = false) {
    this.handleLoading(true)

    if (reset) {
      this.sortField = "id"
      this.sortOrder = 0
    }

    const params: any = {
      page: reset ? 0 : this.page,
      size: this.rows,
      sort: `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`,
      "roles": `[${ROLE_BUYER},${ROLE_OWNER}]`,
      active: this.status === 'null' ? null : this.status === 'true'
    };

    if (this.user?.includes('@')) {
      params.email = this.user;
    } else {
      params.name = this.user;
    }

    this.compradorService.findAll(params).subscribe({
      next: (response) => {
        this.compradorList = response.body?.user?.userList ?? []
        this.totalItems = Number(response.body?.user.totalCount);
        this.loadCustomerNames();
        this.handleLoading(false)
      },
      error: () => {
        this.handleLoading(false)
      }
    })
  }

  handleLoading(loading: boolean) {
    if (loading) this.spinner.show(); else this.spinner.hide()
  }

  getBackgroundClasses(active: boolean) {
    if (active) return 'bg-green-100';
    return 'bg-red-100';
  }

  loadCustomerNames() {
    const addressNumbers = this.compradorList
      .map((user: any) => user.addressNumber || user.user?.addressNumber)
      .filter(an => an !== null && an !== undefined);

    if (addressNumbers.length === 0) {
      return;
    }

    this.customerService.getCustomerDataByANs(addressNumbers).subscribe({
      next: (response) => {
        const customers = (response.body as any)?.customers ?? [];
        this.customerNameMap.clear();
        customers.forEach((customer: any) => {
          this.customerNameMap.set(customer.an, customer.name);
        });
      },
      error: (err) => {
      }
    });
  }

  getCustomerName(user: any): string {
    const addressNumber = user?.addressNumber || user?.user?.addressNumber;
    const name = this.customerNameMap.get(addressNumber) ?? `Cliente ${addressNumber}`;
    return name;
  }

  openEditModal(id: number | null) {
    const dialogRef = this.dialog.open(BuyerUpdate, {
      width: '30vw',
      height: '60vh',
      minWidth: '400px',
      maxWidth: '900px',
      maxHeight: '90vh',
      data: id ? {
        id: id
      } : null,
      panelClass: 'lexend-dialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.findAll(true)
        this.messageService.showSuccess("Usuario creado con exito")
      }
    });
  }

  openDeleteModal(name: string, id: number, active: boolean) {
    const dialogRef = this.dialog.open(BuyerDelete, {

      data: name ? {
        name: name,
        active: active
      } : null,
      panelClass: 'lexend-dialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.deleteUser(id, active);
      }
    });
  }

  deleteUser(id: number, currentStatus: boolean) {
    this.handleLoading(true);
    this.compradorService.delete(id).subscribe({
      next: () => {
        this.findAll();
        const accion = currentStatus ? "desactivado" : "activado";
        this.messageService.showSuccess(`Usuario ${accion} con éxito`);
        this.handleLoading(false);
      },
      error: () => {
        this.handleLoading(false);
      }
    });
  }

  onPageChange(event: PaginatorState) {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 10;
    this.page = Math.floor((this.first ?? 0) / (this.rows ?? 10));
    this.findAll(true);
  }

  loadBuyers(event: TableLazyLoadEvent) {
    if (JSON.stringify(event) === JSON.stringify(this.lastEvent)) {
      return;
    }
    this.lastEvent = event;

    this.sortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField ?? 'startDate';
    this.sortOrder = event.sortOrder ?? 1;
    this.page = Math.floor((event.first ?? 0) / (event.rows ?? 10));
    this.rows = event.rows ?? 10;

    this.findAll();
  }

  cleanFilter() {
    this.user = null;
    this.status = 'true';
    this.findAll(true);
  }

  getBackgroundRoles(roleCode: string) {
    switch (roleCode) {
      case ROLE_OWNER: {
        return 'bg-orange-100'
      }
      case ROLE_BUYER: {
        return 'bg-blue-100'
      }
      default: {
        return 'bg-gray-300'
      }
    }

  }
}
